import {
  Button,
  Cross,
  ErrorExclamation,
} from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import {
  API_ENDPOINT,
  getRecaptchaSiteKey,
} from "../../../../constants/appConstant";
import { useNetwork } from "../../../../hooks/useNetwork";
import useRecaptchaV3 from "../../../../hooks/useRecaptchaV3";
import { ChooseVerificationProps } from "../../../../types";
import { getSiteProps } from "../../../../utils/siteProps";
import { encryptPayload } from "../../../utils/encryptionUtils";

export const ChooseVerification = ({
  updateStepHandler,
  toast,
  mobileNumber,
  countryCode,
  email,
  isEmailVerified,
  isMobileVerified,
  updateOTPIdHandler,
  showEmailVerification = true,
  isRecaptchaEnabled = true,
  onCTA,
  closeApop,
}: ChooseVerificationProps) => {
  const [isNotFromRexmonster, setIsNotFromRexmonster] = useState<boolean>(
    showEmailVerification,
  );
  const { response: apiResponse, fetchData: fetchAPI } = useNetwork();
  const executeRecaptcha = useRecaptchaV3(
    getRecaptchaSiteKey(window.location.host),
    isRecaptchaEnabled,
  );

  const verifyPhoneHandler = async () => {
    let { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("addnumber");
    const apiData = isRecaptchaEnabled
      ? {
          username: `+${countryCode}${mobileNumber}`,
          reCaptchaToken,
          reCaptchaAction,
        }
      : { username: `+${countryCode}${mobileNumber}` };

    const encryptedPayload = encryptPayload(apiData);

    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.sendOTPMiddleware}`,
      "POST",
      {},
      { payload: encryptedPayload },
    );

    if ((res?.error as { status?: number })?.status === 401) {
      window.location.href = "/rio/logout";
      return;
    }

    const id = (res?.data as any)?.id;

    if (id) {
      updateStepHandler("MOBILE_OTP");
      updateOTPIdHandler(id);
      toast({
        showToast: true,
        text: "OTP sent successfully",
        type: "success",
      });
      onCTA && onCTA("VERIFICATION");
    } else {
      toast({
        showToast: true,
        text: "Something went wrong!",
        type: "error",
      });
    }
  };

  const verifyEmailHandler = async () => {
    const apiData = { email: email };

    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.sendEmailOTPMiddleware}`,
      "POST",
      {},
      apiData,
    );

    if ((res?.error as { status?: number })?.status === 401) {
      window.location.href = "/rio/logout";
      return;
    }

    const id = (res?.data as any)?.id;

    if (id) {
      updateStepHandler("EMAIL_OTP");
      updateOTPIdHandler(id);
      toast({
        showToast: true,
        text: "OTP sent successfully",
        type: "success",
      });
      onCTA && onCTA("VERIFICATION");
    } else {
      toast({
        showToast: true,
        text: "Something went wrong!",
        type: "error",
      });
    }
  };

  useEffect(() => {
    const siteConfig = getSiteProps(window.location.hostname);
    if (siteConfig?.site_map === "rexmonster") {
      setIsNotFromRexmonster(false);
    }
  }, []);

  let mobileNumberVerificationButton: JSX.Element | null = null;

  if (!isMobileVerified) {
    if (mobileNumber) {
      mobileNumberVerificationButton = (
        <Button
          className="l1-w-full l1-max-w-[calc(100%-72px)] l1-py-3"
          label="Verify Mobile Number"
          modifier="primary"
          type="button"
          onClick={() => verifyPhoneHandler()}
          isLoading={apiResponse?.isLoading}
        />
      );
    } else {
      mobileNumberVerificationButton = (
        <Button
          className="l1-w-full l1-max-w-[calc(100%-72px)] l1-py-3"
          label="Add Mobile Number"
          modifier="primary"
          type="button"
          onClick={() => updateStepHandler("ADD_NUMBER")}
          isLoading={apiResponse?.isLoading}
        />
      );
    }
  }

  return (
    <div
      className="l1-flex l1-flex-col l1-gap-8 l1-relative"
      id="chooseVerificationForm"
    >
      {/* This is added to hide top rounded corner in Modal */}
      {window.innerWidth < 768 ? (
        <>
          <div className="l1-fixed !l1-h-4 l1-bg-white left-0 top-0 w-full"></div>
          <button onClick={closeApop} className="l1-size-6 l1-ml-auto">
            <Cross width={24} height={24} className="l1-size-6" />
          </button>
        </>
      ) : null}
      <div className="l1-mt-5 l1-flex l1-flex-col l1-items-center l1-justify-center l1-gap-2">
        <ErrorExclamation
          width={72}
          height={72}
          className="l1-fill-orange-400"
        />
        <p className="l1-mx-8 l1-text-center l1-text-lg l1-font-semibold">
          To apply for jobs, please verify your{" "}
          {isNotFromRexmonster && email && !isEmailVerified
            ? "email ID or "
            : ""}{" "}
          <br />
          mobile number!
        </p>
      </div>

      <div className="l1-mb-5 l1-flex l1-flex-col l1-items-center l1-justify-center l1-gap-2">
        {isNotFromRexmonster && email && !isEmailVerified ? (
          <Button
            className="!l1-w-full !l1-max-w-[calc(100%-72px)] !l1-py-3"
            label="Verify Email ID"
            modifier="primary"
            type="button"
            onClick={() => verifyEmailHandler()}
            isLoading={apiResponse?.isLoading}
          />
        ) : null}

        {/* Only show if both email and mobile is not verified and site context is not rexmonster */}
        {isNotFromRexmonster &&
        email &&
        !isEmailVerified &&
        mobileNumberVerificationButton ? (
          <div className="l1-flex l1-items-center">
            <div className="l1-h-[0.5px] l1-w-[178px] l1-bg-slate-400" />
            <p className="l1-mx-2 l1-text-slate-400">OR</p>
            <div className="l1-h-[0.5px] l1-w-[178px] l1-bg-slate-400" />
          </div>
        ) : null}

        {mobileNumberVerificationButton}
      </div>
    </div>
  );
};
